import { useState } from 'react';
import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfDay, endOfDay } from 'date-fns';
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { cn } from '../lib/utils';

const PRESETS = [
  { label: 'Today', key: 'today' },
  { label: 'Yesterday', key: 'yesterday' },
  { label: 'Last 7 Days', key: 'last7' },
  { label: 'Last 30 Days', key: 'last30' },
  { label: 'This Month', key: 'this_month' },
  { label: 'Last Month', key: 'last_month' },
  { label: 'All Time', key: 'all_time' },
];

function getPresetRange(key) {
  const now = new Date();
  switch (key) {
    case 'today':
      return { from: startOfDay(now), to: endOfDay(now) };
    case 'yesterday': {
      const y = subDays(now, 1);
      return { from: startOfDay(y), to: endOfDay(y) };
    }
    case 'last7':
      return { from: startOfDay(subDays(now, 6)), to: endOfDay(now) };
    case 'last30':
      return { from: startOfDay(subDays(now, 29)), to: endOfDay(now) };
    case 'this_month':
      return { from: startOfMonth(now), to: endOfDay(now) };
    case 'last_month': {
      const lm = subMonths(now, 1);
      return { from: startOfMonth(lm), to: endOfMonth(lm) };
    }
    case 'all_time':
    default:
      return { from: null, to: null };
  }
}

export function DateRangeSelector({ onRangeChange, defaultPreset = 'last30' }) {
  const [open, setOpen] = useState(false);
  const [activePreset, setActivePreset] = useState(defaultPreset);
  const [dateRange, setDateRange] = useState(() => getPresetRange(defaultPreset));
  const [calendarMode, setCalendarMode] = useState(false);

  const handlePresetClick = (key) => {
    const range = getPresetRange(key);
    setActivePreset(key);
    setDateRange(range);
    setCalendarMode(false);
    setOpen(false);
    onRangeChange?.(range.from ? format(range.from, 'yyyy-MM-dd') : null, range.to ? format(range.to, 'yyyy-MM-dd') : null);
  };

  const handleCalendarSelect = (range) => {
    if (!range) return;
    setDateRange(range);
    if (range.from && range.to) {
      setActivePreset('custom');
      setOpen(false);
      onRangeChange?.(format(range.from, 'yyyy-MM-dd'), format(range.to, 'yyyy-MM-dd'));
    }
  };

  const getDisplayLabel = () => {
    if (activePreset === 'custom' && dateRange.from && dateRange.to) {
      return `${format(dateRange.from, 'MMM d')} - ${format(dateRange.to, 'MMM d, yyyy')}`;
    }
    const preset = PRESETS.find(p => p.key === activePreset);
    return preset?.label || 'Select Range';
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("h-9 gap-2 text-sm font-medium", activePreset !== 'all_time' && "text-foreground")}
          data-testid="date-range-selector"
        >
          <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
          {getDisplayLabel()}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <div className="flex">
          {/* Presets sidebar */}
          <div className="border-r p-2 space-y-0.5 min-w-[150px]">
            {PRESETS.map(preset => (
              <button
                key={preset.key}
                onClick={() => handlePresetClick(preset.key)}
                data-testid={`date-preset-${preset.key}`}
                className={cn(
                  "w-full text-left text-sm px-3 py-2 rounded-md transition-colors duration-150",
                  activePreset === preset.key
                    ? "bg-primary text-primary-foreground font-medium"
                    : "hover:bg-muted text-foreground"
                )}
              >
                {preset.label}
              </button>
            ))}
            <div className="border-t my-1.5" />
            <button
              onClick={() => { setCalendarMode(true); setActivePreset('custom'); }}
              data-testid="date-preset-custom"
              className={cn(
                "w-full text-left text-sm px-3 py-2 rounded-md transition-colors duration-150",
                activePreset === 'custom'
                  ? "bg-primary text-primary-foreground font-medium"
                  : "hover:bg-muted text-foreground"
              )}
            >
              Custom Range
            </button>
          </div>
          {/* Calendar (shown when Custom Range is active) */}
          {calendarMode && (
            <div className="p-2">
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={handleCalendarSelect}
                numberOfMonths={2}
                defaultMonth={dateRange.from || subDays(new Date(), 30)}
                disabled={{ after: new Date() }}
                data-testid="date-range-calendar"
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
